import type { User } from '@mend/types/user';
import type { Response } from '#types/common.ts';

import { browserAgent } from '#lib/new-relic.ts';
import { getFullName } from '#utils/user.ts';
import { api } from '../http-clients.ts';

/**
 * A provider the Provider Filter can name: one of the organization's providers or provider admins,
 * or the Unassigned sentinel that appointments with no assigned provider carry.
 *
 * A normalized view rather than a raw payload row, because the two sources don't agree on shape —
 * the sentinel is a fixed record the API synthesizes rather than a stored user, and carries none of
 * the contact or credential fields a real provider does.
 */
export interface DirectoryProvider {
  id: User['id'];
  /** As the app names any user — a preferred name when they have one. */
  name: string;
  role: User['role'] | null;
  credential: string | null;
  specialty: string | null;
  email: string | null;
  mobile: string | null;
  isUnassigned: boolean;
}

/**
 * High enough to be the whole directory for any real organization, matching the portal's own
 * provider reads. The response is virtualized and filtered client-side, so a large organization
 * costs one request per session rather than a slow popup.
 */
const directoryLimit = 10_000;

type GetProvidersResponse = {
  providers: User[];
  /** Present when the index paginates; the whole directory's size, not this page's. */
  totalItems?: number;
};

/**
 * Every provider and provider admin the session's organization can filter by, ordered by last name,
 * with Unassigned first.
 */
export async function getProviderDirectory(): Promise<DirectoryProvider[]> {
  const [unassigned, providers] = await Promise.all([
    // The provider index never returns the sentinel — it's a null user, and those are indexed
    // separately. Both reads have to land: a directory cached for the session without Unassigned in
    // it is the removable-but-never-restorable trap ADR 0004 exists to close, and it would be
    // invisible. Failing outright is retried, and the picker says so.
    getUnassignedProvider(),
    getProviders(),
  ]);

  return unassigned ? [unassigned, ...providers] : providers;
}

async function getProviders(): Promise<DirectoryProvider[]> {
  const response = await api.get<Response<GetProvidersResponse>>('/provider', {
    params: { limit: directoryLimit, order: 'firstName ASC' },
  });
  const { providers, totalItems } = response.data.payload;

  // An organization past the limit would lose people from the picker *and* from chip hydration,
  // with nothing to see: the list would simply be short. Compared against the reported total rather
  // than against the limit, because a directory of exactly `directoryLimit` providers is complete.
  if (totalItems !== undefined && totalItems > providers.length) {
    browserAgent.noticeError(
      `Provider directory truncated: ${providers.length} of ${totalItems} providers returned. ` +
        'The Provider Filter cannot list or name the remainder.'
    );
  }

  return providers.map((provider) => ({
    id: provider.id,
    name: getFullName(provider),
    role: provider.role,
    credential: provider.credentialName,
    specialty: provider.specialtyName,
    email: provider.email,
    mobile: provider.mobile === null ? null : String(provider.mobile),
    isUnassigned: false,
  }));
}

/**
 * The organization's Unassigned sentinel. The endpoint is an index by shape but answers with
 * exactly one record — a fixed constant stamped with the organization id — so a second would have
 * no way to tell itself apart in a picker where both would read "Unassigned".
 */
async function getUnassignedProvider(): Promise<DirectoryProvider | null> {
  const response = await api.get<Response<GetProvidersResponse>>('/provider/null');
  const [provider] = response.data.payload.providers;

  if (!provider) {
    browserAgent.noticeError(
      'The null-provider endpoint returned no sentinel; the Provider Filter cannot offer Unassigned.'
    );
    return null;
  }

  return {
    id: provider.id,
    // The record's own name fields spell "Unassigned Provider", which reads as a person. What staff
    // are picking is the absence of one.
    name: 'Unassigned',
    role: null,
    credential: null,
    specialty: null,
    email: null,
    mobile: null,
    isUnassigned: true,
  };
}
